import React from 'react';
import { X } from 'lucide-react';
import type { CatalogSort } from '@xshop/shared';
import { useElementBox, useElementRender, type ElementComponent } from './elementRenderContext';
import { boolProp, choiceProp } from './elementProps';
import { useListing } from './ListingContext';
import { goToListing, isNarrowed, LISTED_FACETS, withoutNarrowing, type ListingAddress } from './listingAddress';
import { useListingWords, type ListingWords } from './listingWords';
import { money, type StoreFacts } from './storeFacts';
import './elementListingToolbar.css';

// The bar over a listing's grid, as Sleek's: how many products the choices leave on the start side, the
// sort on the end side, and under them each choice in use as a chip that takes itself off, with Remove all.
// The count is the listing's own total, so it agrees with the grid and the pages under it.

/** The sorts offered on a collection page, in Sleek's order; a search swaps the hand order for relevance. */
const COLLECTION_SORTS: readonly CatalogSort[] = ['manual', 'best-selling', 'title-asc', 'title-desc', 'price-asc', 'price-desc', 'newest'];
const SEARCH_SORTS: readonly CatalogSort[] = ['relevance', 'best-selling', 'price-asc', 'price-desc', 'newest'];

/** How the sort menu is drawn: its words alone, or in an outlined box like the filters' dropdowns beside it. */
const SORT_LOOKS = ['plain', 'box'] as const;

/** One choice in use, as a chip: what it says and the address without it. */
type Chip = { key: string; label: string; without: ListingAddress };

// Lists the choices in use as chips: the ticked values, in stock, and the price range.
function chipsOf(address: ListingAddress, words: ListingWords, store: StoreFacts | undefined): Chip[] {
  const price = (amount: number) => (store ? money(amount, store) : String(amount));
  const chips: Chip[] = LISTED_FACETS.flatMap((facet) => address[facet].map((value) => ({
    key: `${facet}:${value}`, label: value, without: { ...address, [facet]: address[facet].filter((entry) => entry !== value), page: 1 }
  })));
  if (address.available) chips.push({ key: 'available', label: words.inStock, without: { ...address, available: false, page: 1 } });
  if (address.priceMin !== null || address.priceMax !== null) {
    const label = `${address.priceMin === null ? '' : price(address.priceMin)} – ${address.priceMax === null ? '' : price(address.priceMax)}`.trim();
    chips.push({ key: 'price', label, without: { ...address, priceMin: null, priceMax: null, page: 1 } });
  }
  return chips;
}

// Draws the sort menu, offering the orders that mean something on this page.
const SortMenu: React.FC<{ sort: CatalogSort; address: ListingAddress; words: ListingWords; search: boolean; collection: boolean }> = ({ sort, address, words, search, collection }) => {
  const offered = (search ? SEARCH_SORTS : COLLECTION_SORTS).filter((entry) => collection || entry !== 'manual');
  return (
    <label className="xe-sort">
      <span>{words.sortBy}:</span>
      <span className="xe-sort-select">
        <select value={offered.includes(sort) ? sort : offered[0]} onChange={(event) => goToListing({ ...address, sort: event.target.value as CatalogSort, page: 1 })}>
          {offered.map((entry) => <option key={entry} value={entry}>{words.sorts[entry]}</option>)}
        </select>
      </span>
    </label>
  );
};

// Draws the choices in use as chips that take themselves off, and Remove all.
const ActiveChips: React.FC<{ address: ListingAddress; words: ListingWords; store: StoreFacts | undefined }> = ({ address, words, store }) => {
  if (!isNarrowed(address)) return null;
  return (
    <ul className="xe-chips">
      {chipsOf(address, words, store).map((chip) => (
        <li key={chip.key}>
          <button type="button" className="xe-chip" aria-label={`${words.remove} ${chip.label}`} onClick={() => goToListing(chip.without)}>
            <span dir="auto">{chip.label}</span><X size={14} aria-hidden="true" />
          </button>
        </li>
      ))}
      <li><button type="button" className="xe-chips-clear" onClick={() => goToListing(withoutNarrowing(address))}>{words.removeAll}</button></li>
    </ul>
  );
};

// Draws the count, the sort and the chips of the page's listing, or nothing on a page without one.
export const ListingToolbarElement: ElementComponent = ({ node }) => {
  const box = useElementBox(node);
  const listing = useListing();
  const words = useListingWords();
  const { store } = useElementRender();
  if (!listing) return null;
  const data = listing.read.data;
  // A search that found nothing says so in the grid's own words; a bar counting zero adds nothing to it.
  if (data && data.total === 0 && !isNarrowed(listing.address)) return null;
  const counted = listing.words ? words.results : words.products;
  return (
    <div {...box} className={[box.className, 'xe-toolbar'].filter(Boolean).join(' ')} data-sort={choiceProp(node.props, 'sortLook', SORT_LOOKS, 'plain')}>
      <div className="xe-toolbar-row">
        <p className="xe-toolbar-count" aria-live="polite">{data ? counted(data.total) : ' '}</p>
        {data && boolProp(node.props, 'showSort', true) ? <SortMenu sort={data.sort} address={listing.address} words={words} search={listing.words !== ''} collection={data.collection !== null} /> : null}
      </div>
      {boolProp(node.props, 'showChips', true) ? <ActiveChips address={listing.address} words={words} store={store ?? undefined} /> : null}
    </div>
  );
};
